import type { CampaignChannel } from './campaign'

/**
 * The sequence as the builder shows it: the steps themselves, the wait between
 * them, and the rail that explains who leaves and when anything may send.
 */

export const CAMPAIGN_STEP_STATES = {
  sent: 'sent',
  scheduled: 'scheduled',
  draft: 'draft',
} as const

export type CampaignStepState =
  (typeof CAMPAIGN_STEP_STATES)[keyof typeof CAMPAIGN_STEP_STATES]

/**
 * One figure in a step's footer strip. Six arrive per step, in render order.
 *
 * The `key` *is* the word under the figure — the service flips the first one
 * from `sent` to `queued` on a step that has not gone out, so translating the
 * key reads correctly without cross-referencing `state`.
 */
export interface CampaignStepStat {
  key: string
  /**
   * `null` when the service has no figure to report. Both the placeholder and
   * its label then read in the faint ink — a figure that does not exist should
   * not draw the eye like one that does.
   */
  value: string | null
  /** Money reads in the darker ink. */
  strong?: boolean
}

/**
 * The pill between two steps: how long, and on what condition. Every step
 * carries one, the last included — there it closes the sequence off rather
 * than pointing at another step ("Then · sequence ends — contact returns to
 * normal follow-up").
 */
export interface CampaignWait {
  /** "Wait 3 days", or "Then" on the closing connector. */
  label: string
  /** "only if no reply, no click and no booking" / "sequence ends — …". */
  condition: string
}

/**
 * A step as `GET /campaigns/{id}/sequence` describes it — every line of the
 * card already worded by the service, down to the pill and the footer. Nothing
 * here is derived: `stateLabel` dates only a step still ahead, and `footerNote`
 * is one of four sentences the service picks between.
 */
export interface CampaignSequenceStep {
  id: string
  position: number
  channel: CampaignChannel
  /** "Sends immediately on enrolment", "3 days after step 1". */
  timing: string
  state: CampaignStepState
  /** "Sent", "Sends Aug 6" — the pill on the right of the step header. */
  stateLabel: string
  /** Email steps that open a thread. Absent when the step replies into one. */
  subject?: string
  /**
   * Why this step has no subject of its own. Shown as a dotted note above the
   * body — it is the single most misread thing about a follow-up step.
   */
  threadNote?: string
  /** Message text, with `{{merge_field}}` tokens left in place. */
  body: string
  /**
   * The line under the message: an SMS counts characters and segments, an
   * email says which thread it replies inside, that it is the breakup, or how
   * many merge fields it carries **and whether the sending domain is
   * verified**. That last one is not decoration — unverified mail goes to
   * spam, so the sentence is rendered as sent rather than trimmed.
   */
  footerNote: string
  /** A retired step keeps its history but no longer sends. */
  isActive: boolean
  /** Six figures, in the service's order. */
  stats: CampaignStepStat[]
  /** The connector under this step. The last one closes the sequence. */
  wait: CampaignWait
}

/** An exit rule or a sending-window switch. */
export interface CampaignSequenceSwitch {
  key: string
  label: string
  note: string
  enabled: boolean
  /** Compliance switches cannot be turned off — they render locked. */
  locked?: boolean
}
